/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, September 26th 2021, 10:49:42 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { UUID4, PrimaryKeyValue } from "lib/types";
import { WritableModel } from "lib/schemas/abstract";
import {
  MailServer,
  DesignLayout,
  ProcessTemplateArtifactType,
  FormConfiguration,
  OTPPortalSettings,
} from "lib/schemas";
import { schemaBuilder, isWebHost, Many2ManyUUIDPKBaseSchema } from "lib/validation";

export interface Portal extends WritableModel<false> {
  name: string;
  domain: string;
  organization: UUID4;
  readonly organization_name?: string;
  readonly portal_url?: string;
  readonly has_custom_design_theme?: boolean;
  google_analytics_key?: string;
  embedded_backend?: boolean;
  show_faqs?: boolean;
  theme_folder?: string;
  base_html_head?: string;
  base_html_design_before_content?: string;
  base_html_design_after_content?: string;
  base_html_scripts?: string;
  home_html_design?: string;
  activation_email?: string;
  default_to_email?: string;
  pilot_password?: string;
  api_link?: string;
  report_link?: string;
  email_notifications_layout?: PrimaryKeyValue<ProcessTemplateArtifactType<DesignLayout>>;
  api_local?: string;
  mail_server?: PrimaryKeyValue<MailServer>;
  sign_on?: number;
  require_two_factor_authentication?: boolean;
  portal_ui_configuration?: {
    menus: {
      [key: string]: { title: string; [key: string]: any };
    };
    [key: string]: any;
  };
  onboarding_forms?: FormConfiguration["id"][];
  otp_settings?: OTPPortalSettings;
  extra_settings?: Record<string, any>;
  tags?: UUID4[];
}

type WriteOnlyPortal = Omit<
  Portal,
  "has_custom_design_theme" | "organization_name" | "portal_url" | "onboarding_forms"
>;

export const PortalValidationSchema: schemaBuilder.SchemaOf<WriteOnlyPortal> = schemaBuilder.object().shape({
  id: schemaBuilder.number().positive().nullable(),
  name: schemaBuilder.string().required("The Name for the portal is required."),
  domain: schemaBuilder.string().required("The host name is required").test(
    "domain-check",
    // "Host name is invalid. Must be hostname e.g. example.com or IP host e.g. 192.168.0.1:8090",
    "Host name is invalid. Must be hostname e.g. example.onestop.site",
    isWebHost
  ),
  organization: schemaBuilder.string().uuid().required("Organization is Required"),
  google_analytics_key: schemaBuilder.string().optional().nullable(),
  embedded_backend: schemaBuilder.boolean().optional(),
  show_faqs: schemaBuilder.boolean().optional(),
  theme_folder: schemaBuilder.string().optional().nullable(),
  base_html_head: schemaBuilder.string().optional(),
  base_html_design_before_content: schemaBuilder.string().optional(),
  base_html_design_after_content: schemaBuilder.string().optional(),
  base_html_scripts: schemaBuilder.string().optional(),
  home_html_design: schemaBuilder.string().optional(),
  activation_email: schemaBuilder.string().optional(),
  default_to_email: schemaBuilder.string().email("Must be a valid email address").optional().nullable(),
  pilot_password: schemaBuilder.string().optional().nullable(),
  api_link: schemaBuilder.string().url().optional().nullable(),
  report_link: schemaBuilder.string().url().optional().nullable(),
  email_notifications_layout: schemaBuilder.number().optional().nullable(), //DesignLayout;
  api_local: schemaBuilder.string().optional().nullable(),
  mail_server: schemaBuilder.number().optional().nullable(), //MailServer;
  sign_on: schemaBuilder.number().optional().nullable(),
  require_two_factor_authentication: schemaBuilder.boolean().optional(),
  portal_ui_configuration: schemaBuilder.mixed().optional().nullable(),
  otp_settings: schemaBuilder.mixed().optional().nullable(),
  extra_settings: schemaBuilder.mixed().optional().nullable(),
  tags: Many2ManyUUIDPKBaseSchema().optional().nullable(),
});
